import { Injectable, inject, signal } from '@angular/core';
import { LoggingService } from '../core/services/logging.service';

export interface Toast {
  id: number;
  message: string;
  action?: string;
  /** Invoked when the user clicks the action button. Runs before dismissal. */
  onAction?: () => void;
  /** Invoked when the toast dismisses without the action being clicked. */
  onTimeout?: () => void;
  tone: 'default' | 'success' | 'error' | 'info';
  duration: number;
}

let nextId = 0;

/**
 * Drop-in replacement for MatSnackBar.
 *
 * Usage (identical call signature):
 *   this.toast.open('Customer saved', 'OK', { duration: 3000 });
 *   this.toast.open('Failed to save', 'OK', { duration: 5000 });
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly toasts = signal<Toast[]>([]);

  private readonly loggingService = inject(LoggingService);

  /**
   * Show a toast. Compatible with MatSnackBar.open() call sites:
   *   open(message, action?, config?)
   */
  open(
    message: string,
    action?: string,
    config?: {
      duration?: number;
      tone?: 'default' | 'success' | 'error' | 'info';
      onAction?: () => void;
      onTimeout?: () => void;
    },
  ): void {
    const id = nextId++;
    const duration = config?.duration ?? 3000;
    // Tone resolution: explicit config wins; otherwise a message that reads as a
    // failure ("... failed", "error ...") renders as an error, and everything
    // else defaults to success (green). Rationale: the app-wide convention
    // (Style Guide §18 "Toasts — tone resolution") is that a plain
    // `open(msg, 'OK', { duration })` marks a successful mutation — those should
    // give positive green feedback. Genuine neutral/info or non-keyword
    // failures must pass an explicit `tone`.
    const tone: 'default' | 'success' | 'error' | 'info' =
      config?.tone ??
      (message.toLowerCase().includes('fail') || message.toLowerCase().includes('error') ? 'error' : 'success');

    /**
     * @ticket EB-3798
     * Usage hook: every error-toned toast is a user-visible failure signal.
     * The message TEXT is deliberately NOT logged — many error toasts echo a
     * backend message (`err.error.message`) that can embed customer PII
     * (name/email/IBAN/phone); the "Don't log PII" rule wins over richer copy.
     * The event itself is the signal (error surfaced to a user); the technical
     * detail lives in the request-bound `error.http` event. No correlationId at
     * this layer (D10). (Code-review 2026-09-10.)
     *
     * Named `toast.error` (NOT `error.toast`): it rides the USAGE stream by
     * design (D10), so an `error.*` name would be silently missed by the natural
     * `eventType:operational AND short_message:error.*` query. A stable non-PII
     * discriminator (a toast category enum) is a future improvement — the toast
     * layer has no stable message key today. (PR #43 review.)
     */
    if (tone === 'error') {
      this.loggingService.usage('toast.error');
    }

    const toast: Toast = {
      id,
      message,
      action,
      tone,
      duration,
      onAction: config?.onAction,
      onTimeout: config?.onTimeout,
    };
    this.toasts.update(t => [...t, toast]);

    if (duration > 0) {
      setTimeout(() => {
        const stillPresent = this.toasts().find(x => x.id === id);
        if (stillPresent) {
          stillPresent.onTimeout?.();
          this.dismiss(id);
        }
      }, duration);
    }
  }

  /** Called by the action button — runs the callback, then dismisses. */
  triggerAction(id: number): void {
    const toast = this.toasts().find(x => x.id === id);
    toast?.onAction?.();
    this.dismiss(id);
  }

  dismiss(id: number): void {
    this.toasts.update(t => t.filter(x => x.id !== id));
  }
}
